type Placement = 'top' | 'bottom-start' | 'bottom-end';
const EDGE_MARGIN = 8;
const GAP = 8;

export const clampPosition = (
  left: number,
  top: number,
  width: number,
  height: number,
): { left: number; top: number } => ({
  left: Math.max(EDGE_MARGIN, Math.min(left, window.innerWidth - width - EDGE_MARGIN)),
  top: Math.max(EDGE_MARGIN, Math.min(top, window.innerHeight - height - EDGE_MARGIN)),
});

export const positionAtAnchor = (
  anchor: DOMRect,
  width: number,
  height: number,
  placement: Placement,
): { left: number; top: number } => {
  let left = placement === 'top' ? anchor.left + (anchor.width - width) / 2 : anchor.left;
  if (placement === 'bottom-end') left = anchor.right - width;
  const above = anchor.top - height - GAP;
  const below = anchor.bottom + GAP;
  const fitsAbove = above >= EDGE_MARGIN;
  const fitsBelow = below + height <= window.innerHeight - EDGE_MARGIN;
  const preferAbove = placement === 'top';
  const showAbove = preferAbove ? fitsAbove || !fitsBelow : !fitsBelow && fitsAbove;
  const top = showAbove ? above : below;
  return clampPosition(left, top, width, height);
};

/** Keep an oversized menu on the roomier side of its trigger and scroll there. */
export const positionMenuAtAnchor = (
  anchor: DOMRect,
  width: number,
  naturalHeight: number,
  placement: 'bottom-start' | 'bottom-end',
): { left: number; top: number; maxHeight: number } => {
  const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
  const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
  const above = Math.max(
    0,
    Math.min(viewportHeight - EDGE_MARGIN, anchor.top) - GAP - EDGE_MARGIN,
  );
  const below = Math.max(
    0,
    viewportHeight - EDGE_MARGIN - Math.max(EDGE_MARGIN, anchor.bottom) - GAP,
  );
  const showAbove = naturalHeight > below && above > below;
  const maxHeight = Math.max(1, showAbove ? above : below);
  const height = Math.min(naturalHeight, maxHeight);
  const start = anchor.left;
  const end = anchor.right - width;
  const fits = (left: number): boolean =>
    left >= EDGE_MARGIN && left + width <= viewportWidth - EDGE_MARGIN;
  const preferred = placement === 'bottom-end' ? end : start;
  const alternate = placement === 'bottom-end' ? start : end;
  return {
    left: Math.max(
      EDGE_MARGIN,
      Math.min(
        fits(preferred) || !fits(alternate) ? preferred : alternate,
        viewportWidth - width - EDGE_MARGIN,
      ),
    ),
    top: Math.max(
      EDGE_MARGIN,
      Math.min(
        showAbove ? anchor.top - GAP - height : anchor.bottom + GAP,
        viewportHeight - height - EDGE_MARGIN,
      ),
    ),
    maxHeight,
  };
};
